import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import { phoneNumberIncludeKey } from '@/_containers';
import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import type {
  RecruitingCreateSchema,
  RecruitingMutationSchema,
  RecruitingQueryAllSchema,
  RecruitingQuerySchema,
} from './schemas';

const includeKeys = `profile_image,${phoneNumberIncludeKey()},recruiting_status,recruiting_job_categories,hiring_criteria_categories,resume,additional_documents,referral`;

export function getRecruitingAllQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['RECRUITINGS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<RecruitingQueryAllSchema>({
        endpoint: '/api/v1/recruitings',
        params: `${apiParamStr}&include=${includeKeys}`,
      });

      return res;
    },
  };
}

export function useGetAllRecruiting(apiParamStr: string) {
  return useSuspenseQuery(getRecruitingAllQuery(apiParamStr));
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['RECRUITINGS'] });
};

export function useGetRecruiting(id: string) {
  return useSuspenseQuery(getRecruitingQuery(id));
}

export function getRecruitingQuery(id: string) {
  return {
    queryKey: ['RECRUITING', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: RecruitingQuerySchema }>({
        endpoint: `/api/v1/recruitings/${id}`,
        params: `include=${includeKeys}`,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidateRecruitingQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['RECRUITING', id] });
};

export const useGetRecruitingMutation: UseControlledMutation<RecruitingQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['RECRUITING_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<RecruitingQueryAllSchema>({
            endpoint: '/api/v1/recruitings',
            params: { search, per_page },
          });
          return orderBy(res?.data ?? [], ['first_name']) || [];
        },
      });
    },
  });

export const useCreateMutation = () =>
  useMutation<{ data: RecruitingQuerySchema }, unknown, RecruitingCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/recruitings/',
        payload,
      }),
  });

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: RecruitingQuerySchema }, ApiError, RecruitingMutationSchema>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/recruitings/${id || bId}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'governances', id] });
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: RecruitingQuerySchema }, ApiError, Partial<RecruitingMutationSchema>>({
    mutationFn: ({ id, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/recruitings/${id}`,
        method: 'PATCH',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['RECRUITING_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/recruitings/${id}`,
        method: 'DELETE',
      }),
  });
